import { z } from "zod";
import {
  getVwfsLessorMdmNumber,
  getVwfsLessorSiret,
} from "@/lib/cat/vwfs-lessor-siret";
import { mdmNumberSchema } from "@/lib/schemas/non-vwfs-lessors";
import type {
  FundingType,
  OtherLeasingType,
  Prisma,
  VwfsType,
} from "@/prisma/generated/client";

export const SAVE_DRAFT_FIELD_REQUIRED = "Ce champ est obligatoire.";
export const SAVE_DRAFT_POSITIVE_INTEGER =
  "La valeur doit être un entier strictement positif.";

const fundingTypeSchema = z.enum(["VWFS", "OTHER", "CREDIT", "CASH"]);
const vwfsTypeSchema = z.enum(["LOUN", "LLDFI", "LOA", "FINANCE_LEASE"]);
const otherLeasingTypeSchema = z.enum(["LLD", "LOA"]);

const nullableTrimmedString = z.preprocess((value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  if (typeof value !== "string") {
    return value;
  }
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}, z.string().nullable());

function isAbsent(value: string | null | undefined): boolean {
  return value === null || value === undefined;
}

function addRequiredIssue(ctx: z.RefinementCtx, path: (string | number)[]) {
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message: SAVE_DRAFT_FIELD_REQUIRED,
    path,
  });
}

function requirePresent(
  value: string | null | undefined,
  path: (string | number)[],
  ctx: z.RefinementCtx,
) {
  if (isAbsent(value)) {
    addRequiredIssue(ctx, path);
  }
}

function validatePositiveIntegerString(
  value: string | null | undefined,
  path: (string | number)[],
  ctx: z.RefinementCtx,
  required: boolean,
) {
  if (required && isAbsent(value)) {
    addRequiredIssue(ctx, path);
    return;
  }
  if (isAbsent(value)) {
    return;
  }
  const trimmed = value!.trim();
  if (!/^\d+$/.test(trimmed) || Number.parseInt(trimmed, 10) <= 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: SAVE_DRAFT_POSITIVE_INTEGER,
      path,
    });
  }
}

export const saveDraftFundingDataSchema = z
  .object({
    id: z.number(),
    isLeased: z.boolean(),
    fundingType: fundingTypeSchema,
    vwfsType: vwfsTypeSchema.nullable(),
    otherLeasingType: otherLeasingTypeSchema.nullable(),
    businessNumber: nullableTrimmedString,
    lessorSiret: nullableTrimmedString,
    lessorMdmNumber: nullableTrimmedString,
    contractDuration: nullableTrimmedString,
    maxKm: nullableTrimmedString,
    driverFirstName: nullableTrimmedString,
    driverLastName: nullableTrimmedString,
  })
  .superRefine((data, ctx) => {
    if (!data.isLeased) {
      if (data.fundingType !== "CREDIT" && data.fundingType !== "CASH") {
        addRequiredIssue(ctx, ["fundingType"]);
      }
      return;
    }

    if (data.fundingType !== "VWFS" && data.fundingType !== "OTHER") {
      addRequiredIssue(ctx, ["fundingType"]);
      return;
    }

    if (data.fundingType === "VWFS") {
      if (data.vwfsType === null) {
        addRequiredIssue(ctx, ["vwfsType"]);
      }
      requirePresent(data.businessNumber, ["businessNumber"], ctx);
    } else {
      if (data.otherLeasingType === null) {
        addRequiredIssue(ctx, ["otherLeasingType"]);
      }
      requirePresent(data.lessorSiret, ["lessorSiret"], ctx);
      requirePresent(data.lessorMdmNumber, ["lessorMdmNumber"], ctx);
      if (!isAbsent(data.lessorMdmNumber)) {
        const parsed = mdmNumberSchema.safeParse(data.lessorMdmNumber);
        if (!parsed.success) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              parsed.error.issues[0]?.message ??
              "Le n° MDM du loueur est incohérent.",
            path: ["lessorMdmNumber"],
          });
        }
      }
    }

    validatePositiveIntegerString(
      data.contractDuration,
      ["contractDuration"],
      ctx,
      true,
    );
    validatePositiveIntegerString(data.maxKm, ["maxKm"], ctx, true);
  });

export type SaveDraftFundingInput = z.infer<typeof saveDraftFundingDataSchema>;

export type FundingFormLeaseAnswer = "" | "yes" | "no";

export type FundingFormState = {
  isLeased: FundingFormLeaseAnswer;
  fundingType: FundingType | null;
  vwfsType: VwfsType | null;
  otherLeasingType: OtherLeasingType | null;
  businessNumber: string;
  lessorSiret: string;
  lessorMdmNumber: string;
  contractDuration: string;
  maxKm: string;
  driverFirstName: string;
  driverLastName: string;
};

const EMPTY_FUNDING_FORM_STATE: FundingFormState = {
  isLeased: "",
  fundingType: null,
  vwfsType: null,
  otherLeasingType: null,
  businessNumber: "",
  lessorSiret: "",
  lessorMdmNumber: "",
  contractDuration: "",
  maxKm: "",
  driverFirstName: "",
  driverLastName: "",
};

export type DraftFundingFields = {
  isLeased: boolean | null;
  fundingType: FundingType | null;
  vwfsType: VwfsType | null;
  otherLeasingType: OtherLeasingType | null;
  businessNumber: string | null;
  lessorSiret: string | null;
  lessorMdmNumber: string | null;
  contractDuration: string | null;
  maxKm: string | null;
  driverFirstName: string | null;
  driverLastName: string | null;
};

export function mapDraftToFundingFormState(
  draft: DraftFundingFields | null | undefined,
): FundingFormState {
  if (draft === null || draft === undefined) {
    return EMPTY_FUNDING_FORM_STATE;
  }

  const hasSavedFunding =
    draft.isLeased !== null &&
    draft.isLeased !== undefined &&
    draft.fundingType !== null;

  if (!hasSavedFunding) {
    return EMPTY_FUNDING_FORM_STATE;
  }

  return {
    isLeased: draft.isLeased ? "yes" : "no",
    fundingType: draft.fundingType,
    vwfsType: draft.vwfsType,
    otherLeasingType: draft.otherLeasingType,
    businessNumber: draft.businessNumber ?? "",
    lessorSiret: draft.lessorSiret ?? "",
    lessorMdmNumber: draft.lessorMdmNumber ?? "",
    contractDuration: draft.contractDuration ?? "",
    maxKm: draft.maxKm ?? "",
    driverFirstName: draft.driverFirstName ?? "",
    driverLastName: draft.driverLastName ?? "",
  };
}

export function buildFundingDraftUpdate(
  data: SaveDraftFundingInput,
): Prisma.DraftUpdateInput {
  const driverFields = {
    driverFirstName: data.driverFirstName,
    driverLastName: data.driverLastName,
  };

  if (!data.isLeased) {
    return {
      isLeased: false,
      fundingType: data.fundingType,
      vwfsType: null,
      otherLeasingType: null,
      businessNumber: null,
      lessorSiret: null,
      lessorMdmNumber: null,
      contractDuration: null,
      maxKm: null,
      driverFirstName: null,
      driverLastName: null,
    };
  }

  if (data.fundingType === "VWFS") {
    return {
      isLeased: true,
      fundingType: "VWFS",
      vwfsType: data.vwfsType,
      otherLeasingType: null,
      businessNumber: data.businessNumber,
      lessorSiret:
        data.vwfsType !== null ? getVwfsLessorSiret(data.vwfsType) : null,
      lessorMdmNumber:
        data.vwfsType !== null ? getVwfsLessorMdmNumber(data.vwfsType) : null,
      contractDuration: data.contractDuration,
      maxKm: data.maxKm,
      ...driverFields,
    };
  }

  return {
    isLeased: true,
    fundingType: "OTHER",
    vwfsType: null,
    otherLeasingType: data.otherLeasingType,
    businessNumber: data.businessNumber,
    lessorSiret: data.lessorSiret,
    lessorMdmNumber: data.lessorMdmNumber,
    contractDuration: data.contractDuration,
    maxKm: data.maxKm,
    ...driverFields,
  };
}
